import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

export function useBulkDelete(path, reload, label) {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = (ids) => setSelected((s) => (ids.length > 0 && s.length >= ids.length ? [] : ids));
  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Supprimer définitivement ${selected.length} ${label} ?\nCette action est irréversible.`)) return;
    try {
      const { data } = await api.post(path, { ids: selected });
      toast.success(`${data.deleted} élément(s) supprimé(s)`);
      setSelected([]);
      reload();
    } catch (error) {
      toast.error(apiError(error));
    }
  };
  return { selected, toggle, toggleAll, bulkDelete };
}

export function SelectCheckbox({ checked, onChange, testId }) {
  return (
    <input
      type="checkbox"
      data-testid={testId}
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className="h-4 w-4 cursor-pointer rounded border-slate-300 text-brand accent-brand focus:ring-brand"
    />
  );
}

export function BulkDeleteBar({ count, onDelete, testId }) {
  if (!count) return null;
  return (
    <div className="mb-3 flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5" data-testid={testId}>
      <span className="text-xs font-bold text-rose-700">{count} élément(s) sélectionné(s)</span>
      <button
        type="button"
        data-testid={`${testId}-btn`}
        onClick={onDelete}
        className="flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white transition-colors duration-150 hover:bg-rose-700"
      >
        <Trash2 className="h-3.5 w-3.5" /> Supprimer la sélection
      </button>
    </div>
  );
}

export const inputCls =
  "h-11 w-full rounded-lg border border-line px-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";
export const textareaCls =
  "w-full rounded-lg border border-line p-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

export function AdminCard({ title, description, action, children, className = "" }) {
  return (
    <section className={`rounded-2xl border border-line bg-white ${className}`}>
      {(title || action) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <h2 className="font-display text-base font-bold text-ink">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
          </div>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Field({ label, children, hint, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function AdminModal({ open, onOpenChange, title, children, wide = false }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className={`max-h-[92vh] overflow-y-auto ${wide ? "max-w-4xl" : "max-w-2xl"}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-bold text-ink">{title}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function Pill({ children, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-slate-100 text-slate-600",
    blue: "border-brand/20 bg-brand-light text-brand",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function TableWrap({ children }) {
  return <div className="-mx-5 overflow-x-auto px-5">{children}</div>;
}

export function Th({ children, className = "" }) {
  return (
    <th className={`whitespace-nowrap pb-3 pr-4 text-[11px] font-bold uppercase tracking-wide text-slate-400 ${className}`}>
      {children}
    </th>
  );
}

export function Td({ children, className = "" }) {
  return <td className={`py-3 pr-4 text-sm text-ink ${className}`}>{children}</td>;
}
